import React, { useEffect, useMemo, useState } from "react";
import { Loader2, MessageSquare, FileText, Image as ImageIcon, Pencil, Check, X, Search } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { BoardReplyBox } from "../components/BoardReplyBox";

function fmtTs(ts) {
  if (!ts) return "-";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" });
}

function AttachmentLink({ att }) {
  const open = async () => {
    try {
      const { data } = await api.get(`/api/ai-board/attachments/${att.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: att.content_type || data.type }));
      const a = document.createElement("a");
      a.href = url;
      if (att.is_image || att.content_type === "application/pdf") a.target = "_blank";
      else a.download = att.filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Atasament indisponibil");
    }
  };
  return (
    <button
      type="button"
      onClick={open}
      disabled={!att.available}
      data-testid={`istoric-attachment-${att.id}`}
      className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:border-accent-500 hover:text-accent-600 disabled:cursor-not-allowed disabled:opacity-50"
      title={att.available ? "Deschide / descarca" : "Atasament sters"}
    >
      {att.is_image ? <ImageIcon size={12} /> : <FileText size={12} />}
      {att.filename}
    </button>
  );
}

const TABS = [
  { id: "toti", label: "Toti" },
  { id: "buffett", label: "Warren Buffett" },
  { id: "jobs", label: "Steve Jobs" },
  { id: "bezos", label: "Jeff Bezos" },
  { id: "consiliu", label: "Concluzii comune" },
];

function TitleEditor({ group, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(group.title);
  useEffect(() => { setVal(group.title); setEdit(false); }, [group.key, group.title]);
  const save = async () => {
    if (!val.trim()) return;
    try {
      await api.patch(`/api/ai-board/threads/${group.key}`, { title: val.trim() });
      setEdit(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Redenumire esuata");
    }
  };
  if (!edit) {
    return (
      <div className="flex items-center gap-2">
        <div data-testid="istoric-detail-title" className="text-base font-semibold">{group.title}</div>
        {!group.key.startsWith("single-") && (
          <button data-testid="istoric-rename-btn" type="button" onClick={() => setEdit(true)} className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-600 hover:border-accent-500">
            <Pencil size={11} /> Redenumeste
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <input data-testid="istoric-rename-input" value={val} maxLength={120} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm" autoFocus />
      <button data-testid="istoric-rename-save" type="button" onClick={save} className="rounded bg-accent-500 p-1.5 text-ink-950"><Check size={14} /></button>
      <button data-testid="istoric-rename-cancel" type="button" onClick={() => setEdit(false)} className="rounded border border-slate-300 p-1.5"><X size={14} /></button>
    </div>
  );
}

function groupBySession(items) {
  const map = new Map();
  items.forEach((h, i) => {
    const key = h.thread_id || h.session_id || `single-${i}`;
    if (!map.has(key)) map.set(key, { key, consultant_id: h.consultant_id, items: [] });
    map.get(key).items.push(h);
  });
  return Array.from(map.values()).map((g) => {
    const sorted = [...g.items].sort((a, b) => String(a.ts).localeCompare(String(b.ts)));
    const first = sorted[0];
    return { ...g, items: sorted, first, last: sorted[sorted.length - 1], title: first.title || (first.question || "").slice(0, 60) };
  });
}

export default function Istoric() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("toti");

  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/api/ai-board/history");
      setHistory(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Nu pot incarca istoricul");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const allGroups = useMemo(() => groupBySession(history).sort((a, b) => String(b.last.ts).localeCompare(String(a.last.ts))), [history]);
  const inTab = (g, id) => id === "toti" || g.items.some((h) => h.consultant_id === id);
  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allGroups.filter((g) => inTab(g, tab) && (!q || g.title.toLowerCase().includes(q)));
  }, [allGroups, tab, search]);
  const current = groups.find((g) => g.key === selected) || groups[0];
  const count = (id) => allGroups.filter((g) => inTab(g, id)).length;

  return (
    <Layout title="Istoric">
      <div className="mb-4 flex flex-wrap gap-2 border-b border-slate-200 pb-2" data-testid="istoric-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`istoric-tab-${t.id}`}
            onClick={() => { setTab(t.id); setSelected(null); }}
            className={`rounded px-3 py-1.5 text-sm transition-colors ${
              tab === t.id ? "bg-accent-500 font-semibold text-slate-900" : "bg-white text-slate-600 hover:bg-slate-100"
            }`}
          >
            {t.label} <span className="text-xs opacity-70">({count(t.id)})</span>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[340px_1fr]">
        <aside className="space-y-2" data-testid="istoric-list">
          <h3 className="mb-1 flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400">
            <MessageSquare size={12} /> Analize ({groups.length})
          </h3>
          <label className="mb-2 flex items-center gap-2 rounded border border-slate-200 bg-white px-2 py-1.5">
            <Search size={14} className="text-slate-400" />
            <input data-testid="istoric-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cauta dupa denumire analiza..." className="flex-1 text-sm outline-none" />
          </label>
          {loading && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="animate-spin" size={14} /> Se incarca...
            </div>
          )}
          {!loading && groups.length === 0 && <p className="text-xs text-slate-400">Nicio intrebare anterioara.</p>}
          {groups.map((g) => (
            <button
              key={g.key}
              type="button"
              data-testid={`istoric-item-${g.key}`}
              onClick={() => setSelected(g.key)}
              className={`block w-full rounded border p-3 text-left text-xs transition ${
                current?.key === g.key ? "border-accent-500 bg-accent-500/10" : "border-slate-200 bg-white hover:border-slate-400"
              }`}
            >
              <div className="flex justify-between text-[10px] uppercase text-accent-500">
                <span>{g.consultant_id}</span>
                <span className="text-slate-400">{fmtTs(g.last.ts)}</span>
              </div>
              <div data-testid={`istoric-item-title-${g.key}`} className="mt-1 line-clamp-2 font-semibold text-slate-800">{g.title}</div>
              {g.items.length > 1 && <div className="mt-1 text-[10px] text-slate-400">{g.items.length} mesaje</div>}
            </button>
          ))}
        </aside>

        <section data-testid="istoric-detail" className="rounded-lg border border-slate-200 bg-white p-4 min-h-[60vh]">
          {!current ? (
            <p className="text-sm text-slate-400">Selecteaza o conversatie din lista.</p>
          ) : (
            <div className="space-y-4">
              <div className="border-b border-slate-200 pb-2">
                <div className="text-xs uppercase tracking-widest text-slate-400">Analiza</div>
                <TitleEditor group={current} onSaved={load} />
                <div data-testid="istoric-detail-consultant" className="text-xs capitalize text-slate-500">
                  {Array.from(new Set(current.items.map((h) => h.consultant_id))).join(", ")}
                </div>
              </div>
              {current.items.map((h, i) => (
                <div key={i} className="space-y-2" data-testid={`istoric-msg-${i}`}>
                  <div className="text-[11px] text-slate-400">{fmtTs(h.ts)} <span className="uppercase text-accent-500">{h.consultant_id}</span></div>
                  {(i === 0 || current.items[i - 1].question !== h.question) && (
                    <div className="rounded border border-slate-200 bg-slate-100 p-3 text-sm whitespace-pre-wrap">{h.question}</div>
                  )}
                  {h.attachments?.length > 0 && (
                    <div className="flex flex-wrap gap-2" data-testid={`istoric-attachments-${i}`}>
                      {h.attachments.map((a) => <AttachmentLink key={a.id} att={a} />)}
                    </div>
                  )}
                  <div className="rounded border border-accent-500/30 bg-accent-500/5 p-3 text-sm whitespace-pre-wrap">{h.answer}</div>
                </div>
              ))}
              {!current.key.startsWith("single-") && (
                <BoardReplyBox
                  threadId={current.key}
                  defaultRecipient={current.first.consultant_id === "consiliu" ? "consiliu" : current.first.consultant_id}
                  onDone={() => { setSelected(current.key); load(); }}
                />
              )}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
